import { motion, useInView } from 'framer-motion';
import { useTranslation } from 'next-i18next';
import { useRef, useState } from 'react';

import Header from '@/common/header';
import { FaqAccordion } from '@/components/trial/shared/faq-accordion';
import { PingCounter } from '@/components/trial/shared/ping-counter';
import { bai_jamjuree } from '@/public/assets/fonts/Bai-Jamjuree';
import { jetbrainsMono } from '@/public/assets/fonts/JetBrains-Mono';
import { outfit } from '@/public/assets/fonts/Outfit';

import { CreateAccountModal } from '../../create-account-modal';
import CreateAccountV2 from '../../create-account-v2';
import { Footer } from '../../footer';
import { NoPingAppVideo } from '../../shared/noping-app-video';
import type { DefaultTrialProps } from '../../trial-layout-switcher';
import { GradientBg, PingGridBg } from './components/backgrounds';
import { ACCENT_BLUE, APP_FEATURES, DARK_BG, EASE_OUT, FAQ_ITEMS_COUNT, HERO_BADGES_COUNT, HOW_IT_WORKS_STEPS, MUTED_GRAY, NOPING_GREEN, STAT_CARDS, TEXT_LIGHT } from './components/constants';
import { BeforeAfterComparison, GameCarousel, MulticonnectionVisual, MultiInternetVisual } from './components/interactive';
import { Section, SectionTitle, ThemedCard } from './components/ui-helpers';

/* ============================================================
   HELPER COMPONENTS (small, page-specific)
   ============================================================ */

function HowItWorksStep({ step, icon, index, title, description }: { step: number; icon: string; index: number; title: string; description: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <motion.div ref={ref} className='relative flex flex-col items-center text-center' initial={{ opacity: 0, y: 40 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ delay: index * 0.15, duration: 0.5, ease: EASE_OUT }}>
      <motion.div className='relative mb-4 flex size-20 items-center justify-center rounded-full border-2' style={{ borderColor: ACCENT_BLUE, background: `${ACCENT_BLUE}15` }} animate={isInView ? { borderColor: [ACCENT_BLUE, NOPING_GREEN, ACCENT_BLUE] } : {}} transition={{ duration: 3, repeat: Infinity, delay: index * 0.3 }}>
        <span className='text-3xl'>{icon}</span>
        <div className='absolute -end-2 -top-2 flex size-7 items-center justify-center rounded-full text-xs font-black' style={{ background: NOPING_GREEN, color: DARK_BG, fontFamily: 'var(--font-jetbrains)' }}>
          {step}
        </div>
      </motion.div>
      <h3 className='mb-2 text-xl font-bold text-white' style={{ fontFamily: 'var(--font-outfit)' }}>
        {title}
      </h3>
      <p className='max-w-[240px] text-sm' style={{ color: MUTED_GRAY }}>
        {description}
      </p>
    </motion.div>
  );
}

function ConnectingLine({ index }: { index: number }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <div ref={ref} className='hidden max-w-[120px] flex-1 items-center justify-center md:flex'>
      <motion.div className='h-[2px] w-full rounded-full' style={{ background: `linear-gradient(90deg, ${ACCENT_BLUE}, ${NOPING_GREEN})` }} initial={{ scaleX: 0 }} animate={isInView ? { scaleX: 1 } : {}} transition={{ delay: index * 0.2 + 0.15, duration: 0.5, ease: EASE_OUT }} />
    </div>
  );
}

function PowerUpCard({ stat, label, icon, index }: { stat: string; label: string; icon: string; index: number }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <motion.div ref={ref} className='group relative' initial={{ opacity: 0, rotateX: -20, y: 40 }} animate={isInView ? { opacity: 1, rotateX: 0, y: 0 } : {}} transition={{ delay: index * 0.1, type: 'spring', stiffness: 200 }}>
      <div className='relative overflow-hidden rounded-xl border p-6 text-center transition-all duration-300 hover:shadow-lg' style={{ borderColor: `${ACCENT_BLUE}30`, background: `linear-gradient(to bottom, ${ACCENT_BLUE}10, #12121A)` }}>
        <div className='absolute start-0 top-0 size-4 rounded-ss-lg border-s-2 border-t-2' style={{ borderColor: `${ACCENT_BLUE}50` }} />
        <div className='absolute end-0 top-0 size-4 rounded-se-lg border-e-2 border-t-2' style={{ borderColor: `${ACCENT_BLUE}50` }} />
        <div className='absolute bottom-0 start-0 size-4 rounded-es-lg border-b-2 border-s-2' style={{ borderColor: `${ACCENT_BLUE}50` }} />
        <div className='absolute bottom-0 end-0 size-4 rounded-ee-lg border-b-2 border-e-2' style={{ borderColor: `${ACCENT_BLUE}50` }} />
        <div className='mb-3 text-3xl'>{icon}</div>
        <div className='mb-1 text-2xl font-black md:text-3xl' style={{ color: NOPING_GREEN, fontFamily: 'var(--font-outfit)' }}>
          {stat}
        </div>
        <div className='text-sm uppercase tracking-wider' style={{ color: MUTED_GRAY }}>
          {label}
        </div>
      </div>
    </motion.div>
  );
}

/* ============================================================
   MAIN COMPONENT
   ============================================================ */

interface PingTesterProps extends DefaultTrialProps {}

export function PingTester({ daysTrial, trialConfigId, trialPageId, isDisabledButton = false }: PingTesterProps) {
  const { t } = useTranslation(['components/trial/lp/v2b']);
  const [modalOpen, setModalOpen] = useState(false);

  const faqItems = Array.from({ length: FAQ_ITEMS_COUNT }, (_, i) => ({
    q: t(`faq.items.${i}.q`),
    a: t(`faq.items.${i}.a`),
  }));

  return (
    <div className={`${bai_jamjuree.className} ${outfit.variable} ${jetbrainsMono.variable} relative min-h-screen`} style={{ background: DARK_BG, color: TEXT_LIGHT }}>
      {/* FIXED NAVBAR */}
      <Header variant='primary' />

      {/* HERO SECTION */}
      <section id='hero' className='relative flex min-h-screen items-center px-4 pt-20 md:px-8' style={{ zIndex: 1 }}>
        <PingGridBg />

        <div className='relative mx-auto grid w-full max-w-7xl grid-cols-1 items-stretch gap-8 lg:grid-cols-2 lg:gap-12' style={{ zIndex: 1 }}>
          {/* LEFT: Headline + Ping Tester */}
          <div>
            <motion.div className='mb-6 inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-wider' style={{ background: `${NOPING_GREEN}15`, border: `1px solid ${NOPING_GREEN}30`, color: NOPING_GREEN, fontFamily: 'var(--font-jetbrains)' }} initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE_OUT }}>
              <span className='size-1.5 animate-pulse rounded-full bg-[#93FF18]' />
              {t('hero.badge')}
            </motion.div>

            <motion.h1 className='mb-4 text-4xl font-bold leading-tight md:text-5xl lg:text-6xl' style={{ fontFamily: 'var(--font-bai)', color: TEXT_LIGHT }} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.5, ease: EASE_OUT }}>
              {t('hero.title_prefix')} <span className='bg-gradient-to-r from-[#2ECDFF] to-[#2ECDFF]/80 bg-clip-text text-transparent'>{t('hero.title_accent')}</span>
            </motion.h1>

            <motion.p className='mb-6 max-w-lg text-lg leading-relaxed' style={{ color: MUTED_GRAY, fontFamily: 'var(--font-outfit)' }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.4, ease: EASE_OUT }}>
              {t('hero.description')}
            </motion.p>

            <motion.div className='mb-6' initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.3, ease: EASE_OUT }}>
              <PingCounter startPing={187} endPing={5} duration={2.5} accentColor={NOPING_GREEN} size='lg' labelOptimizing={t('ping.optimizing')} labelDone={t('ping.done')} />
            </motion.div>

            <div className='relative overflow-hidden rounded-xl border' style={{ borderColor: `${NOPING_GREEN}30` }}>
              <NoPingAppVideo />
            </div>
          </div>

          {/* RIGHT: CTA + Certifications */}
          <motion.div id='hero-form' className='relative flex flex-col pb-6' initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.3, ease: EASE_OUT }}>
            <ThemedCard className='flex flex-1 flex-col justify-center p-6 md:p-8' glow={NOPING_GREEN} opaque>
              <h2 className='mb-2 text-center text-2xl font-bold md:text-3xl' style={{ fontFamily: 'var(--font-bai)', color: TEXT_LIGHT }}>
                {t('hero.form_title')}
              </h2>
              <p className='mb-5 text-center text-sm' style={{ color: MUTED_GRAY, fontFamily: 'var(--font-outfit)' }}>
                {t('hero.form_subtitle')}
              </p>

              <CreateAccountV2 daysTrial={daysTrial} customTrialData={{ custom: trialConfigId, trialPageId }} isDisabledButton={isDisabledButton} formId='v2b-hero-form' />

              {/* Security certifications */}
              <div className='mt-4 flex items-center justify-center gap-5 pt-4' style={{ borderTop: `1px solid ${ACCENT_BLUE}15` }}>
                <span className='text-[10px] uppercase tracking-wider' style={{ color: MUTED_GRAY, fontFamily: 'var(--font-outfit)' }}>
                  {t('hero.certified_by')}
                </span>
                <div className='flex items-center gap-1.5'>
                  <svg width='16' height='16' viewBox='0 0 21 21' className='shrink-0'>
                    <rect x='1' y='1' width='9' height='9' fill='#F25022' />
                    <rect x='11' y='1' width='9' height='9' fill='#7FBA00' />
                    <rect x='1' y='11' width='9' height='9' fill='#00A4EF' />
                    <rect x='11' y='11' width='9' height='9' fill='#FFB900' />
                  </svg>
                  <span className='text-[10px] font-semibold' style={{ color: '#A0A0B0', fontFamily: 'var(--font-outfit)' }}>
                    {t('hero.microsoft_certified')}
                  </span>
                </div>
                <div className='flex items-center gap-1.5'>
                  <svg width='16' height='16' viewBox='0 0 24 24' fill='none' className='shrink-0'>
                    <path d='M12 2L3 7v5c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-9-5z' fill='#00A4EF' opacity='0.2' />
                    <path d='M12 2L3 7v5c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-9-5z' stroke='#00A4EF' strokeWidth='1.5' fill='none' />
                    <path d='M9 12l2 2 4-4' stroke='#fff' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                  </svg>
                  <span className='text-[10px] font-semibold' style={{ color: '#A0A0B0', fontFamily: 'var(--font-outfit)' }}>
                    {t('hero.digicert_secured')}
                  </span>
                </div>
              </div>
            </ThemedCard>

            {/* Floating benefit badges */}
            <div className='mt-4 flex flex-wrap justify-center gap-2 px-4 md:absolute md:inset-x-0 md:-bottom-0 md:z-20 md:mt-0 md:gap-3'>
              {Array.from({ length: HERO_BADGES_COUNT }, (_, i) => (
                <div key={i} className='flex items-center gap-1.5 rounded-full border px-3 py-1.5 shadow-lg' style={{ backgroundColor: '#0c0e14', borderColor: `${NOPING_GREEN}40`, boxShadow: `0 0 12px ${NOPING_GREEN}15` }}>
                  <span className='text-xs font-bold' style={{ color: NOPING_GREEN, fontFamily: 'var(--font-bai)' }}>
                    {t(`hero.badges.${i}.value`)}
                  </span>
                  <span className='text-[9px] uppercase tracking-wider' style={{ color: MUTED_GRAY, fontFamily: 'var(--font-outfit)' }}>
                    {t(`hero.badges.${i}.label`)}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECTION: Multiconnection Technology */}
      <Section id='multiconnection' bg={<GradientBg color={ACCENT_BLUE} opacity={5} />}>
        <SectionTitle subtitle={t('sections.multiconnection.subtitle')} title={t('sections.multiconnection.title')} highlightedText={t('sections.multiconnection.highlight')} description={t('sections.multiconnection.description')} accentColor={ACCENT_BLUE} />
        <MulticonnectionVisual />
        <div className='mt-10 text-center'>
          <motion.button onClick={() => setModalOpen(true)} disabled={isDisabledButton} className='inline-block cursor-pointer rounded-xl border-none px-8 py-4 text-sm font-bold' style={{ background: NOPING_GREEN, color: DARK_BG, fontFamily: 'var(--font-bai)', boxShadow: `0 0 20px ${NOPING_GREEN}30` }} whileHover={{ scale: 1.04, boxShadow: `0 0 30px ${NOPING_GREEN}50` }} whileTap={{ scale: 0.96 }}>
            {t('sections.multiconnection.cta')}
          </motion.button>
        </div>
      </Section>

      {/* SECTION: Multi Internet */}
      <Section id='multi-internet' bg={<GradientBg color={ACCENT_BLUE} opacity={3} />}>
        <SectionTitle subtitle={t('sections.multi_internet.subtitle')} title={t('sections.multi_internet.title')} highlightedText={t('sections.multi_internet.highlight')} description={t('sections.multi_internet.description')} accentColor={ACCENT_BLUE} />
        <MultiInternetVisual />
      </Section>

      {/* SECTION: Before & After */}
      <Section id='before-after' bg={<GradientBg color={NOPING_GREEN} opacity={3} />}>
        <SectionTitle subtitle={t('sections.before_after.subtitle')} title={t('sections.before_after.title')} highlightedText={t('sections.before_after.highlight')} description={t('sections.before_after.description')} accentColor={NOPING_GREEN} />
        <BeforeAfterComparison />
      </Section>

      {/* SECTION: NoPing App Experience */}
      <Section id='experience' bg={<GradientBg color={ACCENT_BLUE} opacity={5} />}>
        <SectionTitle subtitle={t('sections.experience.subtitle')} title={t('sections.experience.title')} highlightedText={t('sections.experience.highlight')} description={t('sections.experience.description')} accentColor={ACCENT_BLUE} />

        <div className='flex flex-col items-center gap-12 lg:flex-row'>
          <motion.div className='relative mx-auto w-full max-w-2xl flex-1' initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-60px' }} transition={{ duration: 0.5, ease: EASE_OUT }}>
            <div className='pointer-events-none absolute -inset-6 rounded-3xl bg-[#93FF18]/5 blur-3xl' />
            <div className='overflow-hidden rounded-2xl border shadow-2xl' style={{ borderColor: `${NOPING_GREEN}25`, boxShadow: `0 0 40px ${NOPING_GREEN}10` }}>
              <NoPingAppVideo />
            </div>
          </motion.div>

          <div className='max-w-md flex-1 space-y-6'>
            {APP_FEATURES.map((feat, i) => (
              <motion.div key={i} className='flex items-start gap-4' initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1, duration: 0.4, ease: EASE_OUT }}>
                <div className='flex size-10 shrink-0 items-center justify-center rounded-lg' style={{ background: `${feat.color}15`, border: `1px solid ${feat.color}25` }}>
                  <span className='text-lg'>{feat.icon}</span>
                </div>
                <div>
                  <h3 className='mb-1 text-lg font-bold text-white' style={{ fontFamily: 'var(--font-outfit)' }}>
                    {t(`sections.experience.features.${i}.title`)}
                  </h3>
                  <p className='text-sm' style={{ color: MUTED_GRAY, fontFamily: 'var(--font-outfit)' }}>
                    {t(`sections.experience.features.${i}.description`)}
                  </p>
                </div>
              </motion.div>
            ))}

            <div className='pt-4'>
              <motion.button onClick={() => setModalOpen(true)} disabled={isDisabledButton} className='inline-block cursor-pointer rounded-xl border-none px-8 py-4 text-sm font-bold' style={{ background: NOPING_GREEN, color: DARK_BG, fontFamily: 'var(--font-bai)', boxShadow: `0 0 20px ${NOPING_GREEN}30` }} whileHover={{ scale: 1.04, boxShadow: `0 0 30px ${NOPING_GREEN}50` }} whileTap={{ scale: 0.96 }}>
                {t('sections.experience.cta')}
              </motion.button>
            </div>
          </div>
        </div>
      </Section>

      {/* SECTION: How It Works */}
      <Section id='how-it-works'>
        <SectionTitle subtitle={t('sections.how_it_works.subtitle')} title={t('sections.how_it_works.title')} highlightedText={t('sections.how_it_works.highlight')} accentColor={ACCENT_BLUE} />

        <div className='flex flex-col items-center justify-center gap-8 md:flex-row md:gap-4'>
          {HOW_IT_WORKS_STEPS.map((s, i) => (
            <div key={s.step} className='contents'>
              <HowItWorksStep step={s.step} icon={s.icon} index={i} title={t(`sections.how_it_works.steps.${i}.title`)} description={t(`sections.how_it_works.steps.${i}.description`)} />
              {i < HOW_IT_WORKS_STEPS.length - 1 && <ConnectingLine index={i} />}
            </div>
          ))}
        </div>
      </Section>

      {/* SECTION: Stats */}
      <Section id='stats'>
        <SectionTitle subtitle={t('sections.stats.subtitle')} title={t('sections.stats.title')} highlightedText={t('sections.stats.highlight')} accentColor={ACCENT_BLUE} />
        <div className='grid grid-cols-2 gap-4 md:grid-cols-4'>
          {STAT_CARDS.map((c, i) => (
            <PowerUpCard key={i} stat={t(`sections.stats.cards.${i}.stat`)} label={t(`sections.stats.cards.${i}.label`)} icon={c.icon} index={i} />
          ))}
        </div>
      </Section>

      {/* SECTION: Game Library */}
      <Section id='games' bg={<GradientBg color={NOPING_GREEN} opacity={5} />}>
        <SectionTitle subtitle={t('sections.games.subtitle')} title={t('sections.games.title')} highlightedText={t('sections.games.highlight')} description={t('sections.games.description')} accentColor={NOPING_GREEN} />
        <GameCarousel />
      </Section>

      {/* SECTION: FAQ */}
      <Section id='faq'>
        <div className='mx-auto max-w-2xl'>
          <SectionTitle subtitle={t('faq.subtitle')} title={t('faq.title')} accentColor={MUTED_GRAY} />
          <FaqAccordion items={faqItems} accentColor={NOPING_GREEN} />
        </div>
      </Section>

      {/* SECTION: Final CTA */}
      <section className='relative overflow-hidden px-4 py-32'>
        <div className='pointer-events-none absolute inset-0 bg-gradient-to-t from-[#93FF18]/5 via-transparent to-transparent' />
        <div className='pointer-events-none absolute left-1/2 top-1/2 size-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#2ECDFF]/5 blur-[150px]' />

        <div className='relative mx-auto max-w-3xl text-center'>
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, ease: EASE_OUT }}>
            <h2 className='mb-6 text-4xl font-bold md:text-6xl' style={{ fontFamily: 'var(--font-bai)', color: TEXT_LIGHT }}>
              {t('cta.title_prefix')} <span className='bg-gradient-to-r from-[#2ECDFF] to-[#93FF18] bg-clip-text text-transparent'>{t('cta.title_accent')}</span>?
            </h2>
            <p className='mx-auto mb-10 max-w-lg text-lg' style={{ color: MUTED_GRAY, fontFamily: 'var(--font-outfit)' }}>
              {t('cta.description')}
            </p>

            <motion.button onClick={() => setModalOpen(true)} disabled={isDisabledButton} className='inline-block cursor-pointer rounded-xl border-none px-10 py-5 text-lg font-bold' style={{ background: NOPING_GREEN, color: DARK_BG, fontFamily: 'var(--font-bai)', boxShadow: `0 0 25px ${NOPING_GREEN}40` }} whileHover={{ scale: 1.04, boxShadow: `0 0 35px ${NOPING_GREEN}60` }} whileTap={{ scale: 0.96 }}>
              {t('hero.cta')}
            </motion.button>
            <p className='mt-4 text-xs' style={{ color: MUTED_GRAY, fontFamily: 'var(--font-jetbrains)' }}>
              {t('cta.offer')}
            </p>

            <motion.div className='mt-12 inline-flex items-center gap-3 rounded-2xl px-6 py-3' style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${NOPING_GREEN}20` }} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: 0.2, duration: 0.4, ease: EASE_OUT }}>
              <div className='text-start'>
                <div className='text-xs font-bold uppercase tracking-wider' style={{ color: NOPING_GREEN, fontFamily: 'var(--font-jetbrains)' }}>
                  {t('cta.trusted_label')}
                </div>
                <div className='text-sm font-semibold text-white' style={{ fontFamily: 'var(--font-bai)' }}>
                  {t('cta.trusted_value')}
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Footer text={t('footer', { year: new Date().getFullYear() })} />

      <CreateAccountModal open={modalOpen} onClose={() => setModalOpen(false)} daysTrial={daysTrial} customTrialData={{ custom: trialConfigId, trialPageId }} isDisabledButton={isDisabledButton} formId='v2b-modal-form' />
    </div>
  );
}
